import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Lock,
  LogOut,
  Eye,
  EyeOff,
  Download,
  Users,
  Loader2,
  RefreshCcw,
  ArrowLeft,
  Mail,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  KeyRound,
  CheckCircle2,
  Send,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'mealmatcher_admin_token';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// Crude but useful: extract browser + OS from a user-agent string.
const parseUA = (ua) => {
  if (!ua) return { browser: 'Unknown', os: '', icon: '🖥' };
  const lower = ua.toLowerCase();
  let browser = 'Browser';
  if (lower.includes('edg/')) browser = 'Edge';
  else if (lower.includes('chrome/') && !lower.includes('chromium')) browser = 'Chrome';
  else if (lower.includes('safari/') && !lower.includes('chrome')) browser = 'Safari';
  else if (lower.includes('firefox/')) browser = 'Firefox';
  else if (lower.includes('curl/')) browser = 'curl';
  else if (lower.includes('python') || lower.includes('axios') || lower.includes('node')) browser = 'API client';
  let os = '';
  if (lower.includes('iphone') || lower.includes('ipad') || lower.includes('ios')) os = 'iOS';
  else if (lower.includes('android')) os = 'Android';
  else if (lower.includes('mac os') || lower.includes('macintosh')) os = 'macOS';
  else if (lower.includes('windows')) os = 'Windows';
  else if (lower.includes('linux')) os = 'Linux';
  return { browser, os };
};

const EVENT_META = {
  login_success: { label: 'Signed in', tone: 'good', Icon: CheckCircle2 },
  code_sent: { label: 'Code sent', tone: 'neutral', Icon: Send },
  code_fail: { label: 'Wrong code', tone: 'warn', Icon: ShieldAlert },
  password_fail: { label: 'Wrong password', tone: 'bad', Icon: ShieldAlert },
};

const TONE_CLASS = {
  good: 'bg-emerald-50 text-emerald-700',
  neutral: 'bg-stone-100 text-stone-700',
  warn: 'bg-amber-50 text-amber-700',
  bad: 'bg-rose-50 text-rose-700',
};

const AuditRow = ({ item }) => {
  const meta = EVENT_META[item.event] || { label: item.event, tone: 'neutral', Icon: ShieldCheck };
  const Icon = meta.Icon;
  const { browser, os } = parseUA(item.user_agent);
  return (
    <tr className="hover:bg-stone-50/50" data-testid={`audit-row-${item.event}`}>
      <td className="px-6 py-3">
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${TONE_CLASS[meta.tone]}`}
        >
          <Icon className="h-3 w-3" /> {meta.label}
        </span>
      </td>
      <td className="px-6 py-3 font-mono text-xs text-stone-700">{item.ip || '—'}</td>
      <td className="px-6 py-3 text-stone-700">
        <span className="font-semibold">{browser}</span>
        {os ? <span className="text-stone-500"> · {os}</span> : null}
      </td>
      <td className="px-6 py-3 text-stone-500">{formatDate(item.created_at)}</td>
    </tr>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  // 2FA login state
  const [step, setStep] = useState('password'); // 'password' | 'code'
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [auditItems, setAuditItems] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);

  const headers = (tok) => ({ Authorization: `Bearer ${tok}` });

  const fetchAuditLog = async (tok) => {
    setAuditLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/audit-log?limit=25`, {
        headers: headers(tok),
      });
      setAuditItems(data.items || []);
    } catch (err) {
      // Silent fail — main dashboard remains usable.
      console.warn('audit log fetch failed', err?.response?.status);
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchList = async (tok) => {
    setRefreshing(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/waitlist`, { headers: headers(tok) });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, tok);
      fetchAuditLog(tok);
    } catch (err) {
      if (err?.response?.status === 401) {
        setError('Wrong password. Please try again.');
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      } else {
        setError('Could not load the waitlist. Please try again.');
      }
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-login if a token is stored
  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) {
      // Defer state updates outside the synchronous effect body
      Promise.resolve().then(() => fetchList(stored));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/admin/login/request`, { password });
      setChallengeId(data.challenge_id);
      setMaskedEmail(data.masked_email || '');
      setPassword('');
      setCode('');
      setStep('code');
    } catch (err) {
      const httpStatus = err?.response?.status;
      if (httpStatus === 401) {
        setError('Wrong password.');
      } else if (httpStatus === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
      } else if (httpStatus === 502) {
        setError('Could not send the code. Please try again in a moment.');
      } else {
        setError('Could not log in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/admin/login/verify`, {
        challenge_id: challengeId,
        code,
      });
      const tok = data.token;
      setToken(tok);
      localStorage.setItem(TOKEN_KEY, tok);
      setChallengeId('');
      setCode('');
      setStep('password');
      await fetchList(tok);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const httpStatus = err?.response?.status;
      if (httpStatus === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
      } else {
        setError(typeof detail === 'string' ? detail : 'Could not verify the code.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    // Re-trigger step 1 silently — user re-enters password to be safe.
    setStep('password');
    setChallengeId('');
    setCode('');
    setError('');
  };

  const handleBackToPassword = () => {
    setStep('password');
    setChallengeId('');
    setCode('');
    setError('');
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthed(false);
    setItems([]);
    setTotal(0);
    setStep('password');
    setChallengeId('');
    setCode('');
  };

  const handleCsvDownload = async () => {
    setCsvLoading(true);
    try {
      const res = await axios.get(`${API}/admin/waitlist.csv`, {
        headers: headers(token),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      a.download = `mealmatcher-waitlist-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('CSV download failed.');
    } finally {
      setCsvLoading(false);
    }
  };

  // ------------ LOGIN VIEW ------------
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FFFDF8] flex flex-col">
        <header className="px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="admin-back-home"
            className="inline-flex items-center gap-2 text-stone-600 hover:text-orange-600 text-sm font-semibold"
          >
            <ArrowLeft className="h-4 w-4" /> mealmatcher.no
          </Link>
        </header>
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md">
            <div className="flex items-center justify-center mb-6">
              <div className="h-14 w-14 rounded-2xl bg-stone-900 flex items-center justify-center shadow-lg">
                {step === 'code' ? (
                  <ShieldCheck className="h-6 w-6 text-orange-400" />
                ) : (
                  <Lock className="h-6 w-6 text-orange-400" />
                )}
              </div>
            </div>
            <h1 className="font-display font-extrabold text-3xl text-stone-900 tracking-tight text-center mb-1">
              {step === 'code' ? 'Check your email' : 'Admin'}
            </h1>
            <p className="text-center text-stone-500 text-sm mb-8">
              {step === 'code'
                ? `We sent a 6-digit code to ${maskedEmail || 'your inbox'}. Enter it below to finish signing in.`
                : 'Enter the admin password. We will email a one-time code to confirm.'}
            </p>

            {/* STEP 1 — PASSWORD */}
            {step === 'password' && (
              <form
                onSubmit={handleLogin}
                data-testid="admin-login-form"
                className="bg-white border border-stone-200 rounded-3xl p-6 shadow-sm"
              >
                <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    data-testid="admin-password-input"
                    className="w-full pr-10 pl-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 text-sm font-medium outline-none focus:border-orange-500 focus:bg-white transition-colors"
                    placeholder="••••••••••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    data-testid="admin-password-toggle"
                    className="absolute inset-y-0 right-3 my-auto flex items-center text-stone-400 hover:text-stone-700"
                    aria-label="Toggle password visibility"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {error && (
                  <div
                    data-testid="admin-login-error"
                    className="mt-3 flex items-start gap-2 text-rose-600 text-xs font-semibold"
                  >
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || !password}
                  data-testid="admin-login-submit"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-stone-900 text-white text-sm font-bold hover:bg-stone-800 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending code...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" /> Send login code
                    </>
                  )}
                </button>
                <p className="mt-3 text-[11px] text-stone-400 text-center">
                  Two-factor protected · code sent to admin email
                </p>
              </form>
            )}

            {/* STEP 2 — CODE */}
            {step === 'code' && (
              <form
                onSubmit={handleVerifyCode}
                data-testid="admin-otp-form"
                className="bg-white border border-stone-200 rounded-3xl p-6 shadow-sm"
              >
                <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-2">
                  6-digit code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(digits);
                    setError('');
                  }}
                  data-testid="admin-otp-input"
                  className="w-full text-center font-mono tracking-[0.5em] text-2xl font-bold py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 outline-none focus:border-orange-500 focus:bg-white transition-colors"
                  placeholder="••••••"
                  disabled={loading}
                  autoFocus
                />
                {error && (
                  <div
                    data-testid="admin-otp-error"
                    className="mt-3 flex items-start gap-2 text-rose-600 text-xs font-semibold"
                  >
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  data-testid="admin-otp-submit"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-stone-900 text-white text-sm font-bold hover:bg-stone-800 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Verifying...
                    </>
                  ) : (
                    <>
                      <KeyRound className="h-4 w-4" /> Verify & sign in
                    </>
                  )}
                </button>
                <div className="mt-4 flex items-center justify-between text-[11px]">
                  <button
                    type="button"
                    onClick={handleBackToPassword}
                    data-testid="admin-otp-back"
                    className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-900 font-semibold"
                  >
                    <ArrowLeft className="h-3 w-3" /> Use a different password
                  </button>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={resending}
                    data-testid="admin-otp-resend"
                    className="text-orange-600 hover:text-orange-700 font-semibold disabled:opacity-60"
                  >
                    Didn&apos;t get the code?
                  </button>
                </div>
              </form>
            )}

            <p className="text-center text-[11px] text-stone-400 mt-6">
              MealMatcher · Internal tool
            </p>
          </div>
        </main>
      </div>
    );
  }

  // ------------ DASHBOARD VIEW ------------
  return (
    <div className="min-h-screen bg-[#FFFDF8] flex flex-col" data-testid="admin-dashboard">
      <header className="border-b border-stone-200 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-orange-500 flex items-center justify-center">
              <Lock className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-display font-extrabold text-stone-900 tracking-tight">
                MealMatcher Admin
              </div>
              <Link
                to="/"
                className="text-[11px] text-stone-500 hover:text-orange-600 font-semibold inline-flex items-center gap-1"
              >
                <ArrowLeft className="h-3 w-3" /> Back to site
              </Link>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-bold text-stone-600 hover:bg-stone-100"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-10">
        {/* Stats + actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <div className="rounded-3xl bg-orange-500 text-white p-6">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider opacity-80">
              <Users className="h-4 w-4" /> Subscribers
            </div>
            <div
              data-testid="admin-total-count"
              className="mt-2 font-display font-extrabold text-5xl leading-none tracking-tight"
            >
              {total}
            </div>
            <div className="mt-2 text-sm text-white/80">on the waitlist</div>
          </div>
          <div className="rounded-3xl bg-white border border-stone-200 p-6">
            <div className="text-xs font-bold text-stone-500 uppercase tracking-wider">Latest</div>
            <div className="mt-2 font-display font-bold text-stone-900 truncate">
              {items[0]?.email || '—'}
            </div>
            <div className="mt-1 text-xs text-stone-500">
              {items[0] ? formatDate(items[0].created_at) : 'No signups yet'}
            </div>
          </div>
          <div className="rounded-3xl bg-stone-900 text-white p-6 flex flex-col justify-between">
            <div>
              <div className="text-xs font-bold opacity-80 uppercase tracking-wider">Export</div>
              <div className="mt-1 text-sm text-stone-300">Download the full list as CSV.</div>
            </div>
            <button
              onClick={handleCsvDownload}
              disabled={csvLoading || total === 0}
              data-testid="admin-csv-export"
              className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-400 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
            >
              {csvLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Preparing...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" /> Download CSV
                </>
              )}
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-extrabold text-2xl text-stone-900 tracking-tight">
            All subscribers
          </h2>
          <button
            onClick={() => fetchList(token)}
            disabled={refreshing}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-bold text-stone-700 bg-white border border-stone-200 hover:border-stone-900 disabled:opacity-60"
          >
            {refreshing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCcw className="h-3.5 w-3.5" />
            )}
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-2xl bg-rose-50 border border-rose-100 text-rose-700 text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-stone-200 rounded-3xl overflow-hidden">
          {items.length === 0 ? (
            <div className="px-6 py-16 text-center text-stone-500">
              <Mail className="h-8 w-8 mx-auto text-stone-300 mb-3" />
              <p className="font-semibold">No signups yet.</p>
              <p className="text-sm mt-1">When people join the waitlist, they will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-waitlist-table">
                <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="px-6 py-3 font-bold">Email</th>
                    <th className="px-6 py-3 font-bold">Locale</th>
                    <th className="px-6 py-3 font-bold">Source</th>
                    <th className="px-6 py-3 font-bold">Confirmation</th>
                    <th className="px-6 py-3 font-bold">Signed up</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {items.map((it, i) => (
                    <tr key={it.id || it.email + i} className="hover:bg-stone-50/50">
                      <td className="px-6 py-3 font-semibold text-stone-900">{it.email}</td>
                      <td className="px-6 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-stone-100 text-stone-700">
                          {it.locale || 'no'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-stone-600">{it.source || 'website'}</td>
                      <td className="px-6 py-3">
                        {it.confirmation_sent ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700">
                            sent
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700">
                            pending
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-stone-500">{formatDate(it.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ------------ Audit log ------------ */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-display font-extrabold text-2xl text-stone-900 tracking-tight">
                Login activity
              </h2>
              <p className="text-sm text-stone-500 mt-0.5">
                Last 25 events · auto-purged after 90 days
              </p>
            </div>
            <button
              onClick={() => fetchAuditLog(token)}
              disabled={auditLoading}
              data-testid="admin-audit-refresh"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-bold text-stone-700 bg-white border border-stone-200 hover:border-stone-900 disabled:opacity-60"
            >
              {auditLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCcw className="h-3.5 w-3.5" />
              )}
              Refresh
            </button>
          </div>

          <div className="bg-white border border-stone-200 rounded-3xl overflow-hidden">
            {auditItems.length === 0 ? (
              <div className="px-6 py-12 text-center text-stone-500">
                <ShieldCheck className="h-7 w-7 mx-auto text-stone-300 mb-3" />
                <p className="font-semibold text-sm">No login activity yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-audit-table">
                  <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wider text-stone-500">
                    <tr>
                      <th className="px-6 py-3 font-bold">Event</th>
                      <th className="px-6 py-3 font-bold">IP</th>
                      <th className="px-6 py-3 font-bold">Device</th>
                      <th className="px-6 py-3 font-bold">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {auditItems.map((it, i) => (
                      <AuditRow key={i} item={it} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
